import { createContext, useContext, useEffect, useCallback, useState } from 'react';
import { useAuth } from './AuthContext';
import type { ThemeMode } from '../types/database';
import { setStatusBarStyle } from '../hooks/usePlatform';

// Default theme colors (teal)
const DEFAULT_PRIMARY = '#0d9488';
const DEFAULT_SECONDARY = '#f59e0b';
const DEFAULT_THEME_MODE: ThemeMode = 'system';

// LocalStorage keys for theme caching
const THEME_CACHE_KEY = 'podium_theme_cache';
const THEME_MODE_CACHE_KEY = 'podium_theme_mode';

interface ThemeContextType {
  primaryColor: string;
  secondaryColor: string;
  themeMode: ThemeMode;
  resolvedTheme: 'light' | 'dark'; // The actual applied theme (resolved from 'system')
  updateTheme: (primary: string, secondary: string) => Promise<{ error: Error | null }>;
  updateThemeMode: (mode: ThemeMode) => Promise<{ error: Error | null }>;
  resetToDefault: () => Promise<{ error: Error | null }>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Helper to convert hex to HSL values for generating color variants
function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };
  
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }
  
  return { h: h * 360, s: s * 100, l: l * 100 };
}

// Helper to generate a lighter version of a color
function lightenColor(hex: string, percent: number): string {
  const { h, s, l } = hexToHSL(hex);
  const newL = Math.min(100, l + (100 - l) * (percent / 100));
  return `hsl(${h}, ${s}%, ${newL}%)`;
}

// Helper to generate a darker version of a color  
function darkenColor(hex: string, percent: number): string {
  const { h, s, l } = hexToHSL(hex);
  const newL = Math.max(0, l * (1 - percent / 100));
  return `hsl(${h}, ${s}%, ${newL}%)`;
}

// Apply theme colors to CSS custom properties
function applyThemeColors(primary: string, secondary: string) {
  const root = document.documentElement;
  
  // Primary color and variants
  root.style.setProperty('--color-primary', primary);
  root.style.setProperty('--color-primary-light', lightenColor(primary, 30));
  root.style.setProperty('--color-primary-dark', darkenColor(primary, 15));
  root.style.setProperty('--color-primary-50', lightenColor(primary, 85));
  root.style.setProperty('--color-primary-100', lightenColor(primary, 70));
  root.style.setProperty('--color-primary-200', lightenColor(primary, 55));
  root.style.setProperty('--color-primary-500', primary);
  root.style.setProperty('--color-primary-600', darkenColor(primary, 10));
  root.style.setProperty('--color-primary-700', darkenColor(primary, 20));
  
  // Secondary color and variants
  root.style.setProperty('--color-secondary', secondary);
  root.style.setProperty('--color-secondary-light', lightenColor(secondary, 30));
  root.style.setProperty('--color-secondary-dark', darkenColor(secondary, 15));
  root.style.setProperty('--color-secondary-50', lightenColor(secondary, 85));
  root.style.setProperty('--color-secondary-100', lightenColor(secondary, 70));
  root.style.setProperty('--color-secondary-500', secondary);
  root.style.setProperty('--color-secondary-600', darkenColor(secondary, 10));
}

// Apply dark/light mode to document
function applyThemeMode(mode: 'light' | 'dark') {
  const root = document.documentElement;
  if (mode === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

// Get system preference for dark mode
function getSystemThemePreference(): 'light' | 'dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

// Resolve theme mode to actual light/dark value
function resolveThemeMode(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'system') {
    return getSystemThemePreference();
  }
  return mode;
}

// Get cached theme from localStorage for instant application
function getCachedTheme(): { primary: string; secondary: string } | null {
  try {
    const cached = localStorage.getItem(THEME_CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
}

// Cache theme to localStorage
function cacheTheme(primary: string, secondary: string) {
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ primary, secondary }));
  } catch (e) {
    // Ignore localStorage errors
  }
}

// Get cached theme mode from localStorage
function getCachedThemeMode(): ThemeMode | null {
  try {
    const cached = localStorage.getItem(THEME_MODE_CACHE_KEY);
    if (cached && (cached === 'light' || cached === 'dark' || cached === 'system')) {
      return cached as ThemeMode;
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
}

// Cache theme mode to localStorage
function cacheThemeMode(mode: ThemeMode) {
  try {
    localStorage.setItem(THEME_MODE_CACHE_KEY, mode);
  } catch (e) {
    // Ignore localStorage errors
  }
}

// Apply cached theme immediately on module load (before React renders)
const cachedTheme = getCachedTheme();
if (cachedTheme) {
  applyThemeColors(cachedTheme.primary, cachedTheme.secondary);
}

// Apply cached theme mode immediately
const cachedThemeMode = getCachedThemeMode();
if (cachedThemeMode) {
  const resolvedCachedMode = resolveThemeMode(cachedThemeMode);
  applyThemeMode(resolvedCachedMode);
  // Update native status bar on initial load
  setStatusBarStyle(resolvedCachedMode === 'dark');
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { profile, updateProfile } = useAuth();
  
  const primaryColor = profile?.theme_primary_color || DEFAULT_PRIMARY;
  const secondaryColor = profile?.theme_secondary_color || DEFAULT_SECONDARY;
  const themeMode: ThemeMode = profile?.theme_mode || getCachedThemeMode() || DEFAULT_THEME_MODE;
  
  // Track resolved theme (actual light/dark value)
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => resolveThemeMode(themeMode));
  
  // Apply theme colors whenever they change and cache them
  useEffect(() => {
    applyThemeColors(primaryColor, secondaryColor);
    // Cache the theme for instant application on next page load
    if (profile?.theme_primary_color || profile?.theme_secondary_color) {
      cacheTheme(primaryColor, secondaryColor);
    }
  }, [primaryColor, secondaryColor, profile?.theme_primary_color, profile?.theme_secondary_color]);
  
  // Apply theme mode and listen for system preference changes
  useEffect(() => {
    const resolved = resolveThemeMode(themeMode);
    setResolvedTheme(resolved);
    applyThemeMode(resolved);
    cacheThemeMode(themeMode);
    
    // Update native status bar to match theme
    setStatusBarStyle(resolved === 'dark');
    
    // Listen for system preference changes when in 'system' mode
    if (themeMode === 'system' && typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e: MediaQueryListEvent) => {
        const newResolved = e.matches ? 'dark' : 'light';
        setResolvedTheme(newResolved);
        applyThemeMode(newResolved);
        // Update native status bar when system preference changes
        setStatusBarStyle(newResolved === 'dark');
      };
      
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [themeMode]);
  
  // Update theme colors in database
  const updateTheme = useCallback(async (primary: string, secondary: string) => {
    const { error } = await updateProfile({
      theme_primary_color: primary,
      theme_secondary_color: secondary,
    });
    
    if (!error) {
      // Apply immediately for instant feedback
      applyThemeColors(primary, secondary);
    }
    
    return { error };
  }, [updateProfile]);
  
  // Update theme mode in database
  const updateThemeMode = useCallback(async (mode: ThemeMode) => {
    const { error } = await updateProfile({
      theme_mode: mode,
    });
    
    if (!error) {
      // Apply immediately for instant feedback
      const resolved = resolveThemeMode(mode);
      setResolvedTheme(resolved);
      applyThemeMode(resolved);
      cacheThemeMode(mode);
      // Update native status bar
      setStatusBarStyle(resolved === 'dark');
    }
    
    return { error };
  }, [updateProfile]);
  
  // Reset to default theme
  const resetToDefault = useCallback(async () => {
    const { error } = await updateProfile({
      theme_primary_color: null,
      theme_secondary_color: null,
      theme_mode: null,
    });
    
    if (!error) {
      applyThemeColors(DEFAULT_PRIMARY, DEFAULT_SECONDARY);
      const resolved = resolveThemeMode(DEFAULT_THEME_MODE);
      setResolvedTheme(resolved);
      applyThemeMode(resolved);
      // Update native status bar
      setStatusBarStyle(resolved === 'dark');
      // Clear the cache so default theme is used on next load
      try {
        localStorage.removeItem(THEME_CACHE_KEY);
        localStorage.removeItem(THEME_MODE_CACHE_KEY);
      } catch (e) {
        // Ignore localStorage errors
      }
    }
    
    return { error };
  }, [updateProfile]);
  
  return (
    <ThemeContext.Provider
      value={{
        primaryColor,
        secondaryColor,
        themeMode,
        resolvedTheme,
        updateTheme,
        updateThemeMode,
        resetToDefault,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

// Export defaults for use elsewhere
export { DEFAULT_PRIMARY, DEFAULT_SECONDARY };

